import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { UnknownAction } from 'redux';
import styled from 'styled-components';
import { Product, FlowjoCommons } from '@portal/portal-core';

import { SuperAdminActions } from 'ducks/super-client';
import { Button } from 'components/styled-primitives';
import { ProductVersionActions } from 'ducks/product-version';
import { selectProductVersions, selectModal } from 'selectors/product-version';
import { convertArrayOfObjectsToCSV, downloadCSV, getCountryOptions } from 'utils';
import CreateProductVersionModal from './create-product-version-modal';
import CountryWhitelistEditor from './country-whitelist-editor';
import ProductAssetsEditor from './product-assets-editor';

// Optional status colors not declared on the base styled-components theme.
// Accessed below with a runtime `|| '#...'` fallback; the cast keeps that
// behavior type-safe without widening the shared theme type.
type OptionalThemeColors = {
  success?: string;
  warning?: string;
  primary?: string;
};

// Constants for country list rendering types
const COUNTRY_LIST_TYPE = {
  WHITELIST: 'whitelist',
  BLOCKLIST: 'blocklist',
};

// Minimal shape of a product-version record accessed by this component. The
// index signature keeps it assignable to the duck's `Record<string, unknown>`
// product-version type (e.g. for ProductVersionActions.showEditModal).
type ProductVersion = {
  id: string;
  name: string;
  productVersion: string;
  active: boolean;
  whitelistedCountries: string[];
  [key: string]: unknown;
};

// Modal slice shape (selectModal is typed loosely as Record<string, unknown>).
interface ProductVersionModal {
  isOpen?: boolean;
  productVersion?: ProductVersion | null;
}

// Payload emitted by the create-product-version modal. region/type/releaseDate
// are optional because the modal only supplies a subset of these fields.
interface CreateProductVersionData {
  productNameConstant: string;
  semanticProductVersion: string;
  region?: string;
  type?: string;
  releaseDate?: string;
  whitelistedCountries?: string[];
  active?: boolean;
}

const ProductVersionTable = (): React.JSX.Element => {
  const productVersions = useSelector(selectProductVersions) as ProductVersion[];
  const modal = useSelector(selectModal) as ProductVersionModal;
  const dispatch = useDispatch();

  const fetchProductVersions = (): void => {
    dispatch(SuperAdminActions.fetchProductVersions());
  };
  const createProductVersion = (
    name: string,
    productVersion: string,
    region: string,
    productType: string,
    releaseDate: string,
    whitelistedCountries: string[],
    active: boolean,
  ): void => {
    dispatch(
      SuperAdminActions.createProductVersion(
        name,
        productVersion,
        region,
        productType,
        releaseDate,
        whitelistedCountries,
        active,
      ),
    );
  };
  const updateWhitelist = (id: string, add: string[], remove: string[]): void => {
    dispatch(SuperAdminActions.updateProductWhitelist(id, add, remove));
  };
  const updateAssets = (id: string, assets: unknown): void => {
    dispatch(SuperAdminActions.updateProductAssets(id, assets));
  };
  const updateActive = (id: string, active: boolean): void => {
    dispatch(SuperAdminActions.updateProductActive(id, active));
  };
  const showEditModal = (productVersion: ProductVersion): void => {
    // The duck's action creators return interface types, which lack the index
    // signature required by UnknownAction; assert the (structurally valid) shape.
    dispatch(ProductVersionActions.showEditModal(productVersion) as unknown as UnknownAction);
  };
  const hideModal = (): void => {
    dispatch(ProductVersionActions.hideModal() as UnknownAction);
  };

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssetsModal, setShowAssetsModal] = useState(false);
  const [selectedProductVersion, setSelectedProductVersion] = useState<ProductVersion | null>(null);
  const [productFilter, setProductFilter] = useState('all');

  useEffect(() => {
    fetchProductVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateNew = (): void => {
    setShowCreateModal(true);
  };

  const handleCreateSave = (data: CreateProductVersionData): void => {
    const {
      productNameConstant,
      semanticProductVersion,
      region = Product.DEFAULT_PRODUCT_REGION,
      type = Product.DEFAULT_PRODUCT_TYPE,
      releaseDate,
      whitelistedCountries = [],
      active = false,
    } = data;
    createProductVersion(
      productNameConstant,
      semanticProductVersion,
      region,
      type, // This will be passed as productType to the action
      releaseDate as string,
      whitelistedCountries,
      active,
    );
    setShowCreateModal(false);
  };

  const handleCreateCancel = (): void => {
    setShowCreateModal(false);
  };

  const handleEditWhitelist = (productVersion: ProductVersion): void => {
    showEditModal(productVersion);
  };

  const handleWhitelistSave = (addCountries: string[], removeCountries: string[]): void => {
    if (modal.productVersion) {
      updateWhitelist(modal.productVersion.id, addCountries, removeCountries);
    }
    hideModal();
    // TODO: Show success message
  };

  const handleEditAssets = (productVersion: ProductVersion): void => {
    setShowAssetsModal(true);
    setSelectedProductVersion(productVersion);
  };

  const handleAssetsSave = (assets: unknown): void => {
    if (selectedProductVersion) {
      updateAssets(selectedProductVersion.id, assets);
    }
    setShowAssetsModal(false);
    setSelectedProductVersion(null);
  };

  const handleAssetsCancel = (): void => {
    setShowAssetsModal(false);
    setSelectedProductVersion(null);
  };

  const handleProductFilterChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    setProductFilter(event.target.value);
  };

  const handleToggleActive = (productVersion: ProductVersion): void => {
    const newActive = !productVersion.active;
    updateActive(productVersion.id, newActive);
  };

  const getBlockedCountries = (
    whitelistedCountries: string[] | null | undefined,
  ): string[] => {
    // Get all available country codes (non-embargoed)
    const allCountries = getCountryOptions().map((country: { value: string }) => country.value);

    // Get embargoed country codes
    const { Regional } = FlowjoCommons;
    const embargoedCountries = Object.keys(Regional.embargoedCountries || {});

    // If no whitelist (empty array), only embargoed countries are blocked
    if (!whitelistedCountries || whitelistedCountries.length === 0) {
      return embargoedCountries;
    }

    // Return all countries not in the whitelist, plus embargoed countries
    const blockedNonEmbargoed = allCountries.filter(
      (code: string) => !whitelistedCountries.includes(code),
    );

    // Combine and deduplicate
    return [...new Set([...blockedNonEmbargoed, ...embargoedCountries])].sort();
  };

  const handleExportProductVersions = (versionsToExport: ProductVersion[]): void => {
    const exportData = versionsToExport.map((productVersion) => {
      let whitelistedCountriesText = '';
      let blockedCountriesText = '';

      if (productVersion.active) {
        whitelistedCountriesText = productVersion.whitelistedCountries.join(',');
        const blocked = getBlockedCountries(productVersion.whitelistedCountries);
        blockedCountriesText = blocked.join(',');
      } else {
        const countryText =
          !productVersion.whitelistedCountries || productVersion.whitelistedCountries.length === 0
            ? 'All non-embargoed countries'
            : productVersion.whitelistedCountries.join(', ');
        whitelistedCountriesText = `Version Access Currently Disabled for All Countries. Whitelist does not currently apply. The following country codes are set to be whitelisted when the version is enabled: [${countryText}]`;
        const blocked = getBlockedCountries(productVersion.whitelistedCountries);
        const blockedText =
          blocked.length === 0 ? 'All non-embargoed countries' : blocked.join(', ');
        blockedCountriesText = `The following country codes would be blocked when the version is enabled: [${blockedText}]`;
      }

      return {
        productMarketingName:
          Product.PRODUCT_MARKETING_NAMES[productVersion.name] || productVersion.name,
        productNameConstant: productVersion.name,
        version: productVersion.productVersion,
        versionAccess: productVersion.active ? 'enabled' : 'disabled',
        whitelistedCountries: whitelistedCountriesText,
        blockedCountries: blockedCountriesText,
        addToWhitelist: '',
        removeFromWhitelist: '',
      };
    });

    const csvData = convertArrayOfObjectsToCSV({
      data: exportData,
      keys: [
        'productMarketingName',
        'productNameConstant',
        'version',
        'versionAccess',
        'whitelistedCountries',
        'blockedCountries',
        'addToWhitelist',
        'removeFromWhitelist',
      ],
    });

    // Add version comment at the top
    const csvWithVersion = `# version: 1.0\n${csvData}`;

    const timestamp = new Date().toISOString().split('T')[0];
    const filterSuffix = productFilter !== 'all' ? `-${productFilter}` : '';
    downloadCSV({
      filename: `product-versions-export${filterSuffix}-${timestamp}`,
      csvData: csvWithVersion,
    });
  };

  const handleExportCountries = (): void => {
    const countryOptions = getCountryOptions();
    const exportData = countryOptions.map((country: { value: string; label: string }) => ({
      countryCode: country.value,
      countryName: country.label,
    }));

    const csvData = convertArrayOfObjectsToCSV({
      data: exportData,
      keys: ['countryCode', 'countryName'],
    });

    const timestamp = new Date().toISOString().split('T')[0];
    downloadCSV({
      filename: `country-codes-${timestamp}`,
      csvData,
    });
  };

  const renderCountryList = (
    countries: string[] | null | undefined,
    active = true,
    type: string = COUNTRY_LIST_TYPE.WHITELIST,
  ): React.JSX.Element => {
    if (!active) {
      const countryText =
        !countries || countries.length === 0 ? 'All non-embargoed countries' : countries.join(', ');

      if (type === COUNTRY_LIST_TYPE.BLOCKLIST) {
        return (
          <DisabledVersionText>
            The following country codes would be blocked when the version is enabled: [{countryText}
            ]
          </DisabledVersionText>
        );
      }

      return (
        <DisabledVersionText>
          Version Access Currently Disabled for All Countries. Whitelist does not currently apply.
          The following country codes are set to be whitelisted when the version is enabled: [
          {countryText}]
        </DisabledVersionText>
      );
    }

    if (!countries || countries.length === 0) {
      return <AllCountriesText>All non-embargoed countries allowed</AllCountriesText>;
    }

    return (
      <CountryList>
        {countries.map((code) => (
          <CountryBadge key={code}>{code}</CountryBadge>
        ))}
      </CountryList>
    );
  };

  // Get unique product names for filter
  const uniqueProducts = [
    ...new Set(productVersions.map((productVersion) => productVersion.name)),
  ].sort();

  // Filter product versions based on selected product
  const filteredProductVersions =
    productFilter === 'all'
      ? productVersions
      : productVersions.filter((productVersion) => productVersion.name === productFilter);

  return (
    <Container>
      <ControlBar>
        <LeftSection>
          <Title>Product Version Configuration - Beta</Title>
          <FilterContainer>
            <FilterLabel>Filter by Product:</FilterLabel>
            <FilterSelect
              value={productFilter}
              onChange={handleProductFilterChange}
              data-test="product-filter-select"
            >
              <option value="all">All Products ({productVersions.length})</option>
              {uniqueProducts.map((productName) => {
                const count = productVersions.filter(
                  (productVersion) => productVersion.name === productName,
                ).length;
                const displayName = Product.PRODUCT_MARKETING_NAMES[productName] || productName;
                return (
                  <option key={productName} value={productName}>
                    {displayName} ({count})
                  </option>
                );
              })}
            </FilterSelect>
          </FilterContainer>
        </LeftSection>
        <ButtonGroup>
          <Button
            onClick={() => handleExportProductVersions(filteredProductVersions)}
            disabled={productVersions.length === 0}
          >
            Export Product Versions ({filteredProductVersions.length})
          </Button>
          <Button onClick={handleExportCountries}>Export Country Codes</Button>
          <Button onClick={handleCreateNew}>Create New Product Version</Button>
        </ButtonGroup>
      </ControlBar>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <Th>Product Name</Th>
              <Th>Version</Th>
              <Th>Version Access</Th>
              <Th minWidth="200px">Whitelisted Countries (ISO 2-Char Code)</Th>
              <Th width="280px">Blocked Countries (ISO 2-Char Code)</Th>
              <Th width="180px">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filteredProductVersions.length === 0 ? (
              <tr>
                <Td colSpan={6}>
                  <EmptyState>
                    <EmptyIcon>📦</EmptyIcon>
                    <EmptyText>
                      {productVersions.length === 0
                        ? 'No product versions configured yet.'
                        : 'No product versions match the selected filter.'}
                    </EmptyText>
                    <EmptySubtext>
                      {productVersions.length === 0
                        ? 'Click "Create New Product Version" to get started.'
                        : 'Try selecting a different product or "All Products".'}
                    </EmptySubtext>
                  </EmptyState>
                </Td>
              </tr>
            ) : (
              filteredProductVersions.map((productVersion) => (
                <tr key={productVersion.id}>
                  <Td>
                    <ProductName>
                      {Product.PRODUCT_MARKETING_NAMES[productVersion.name] || productVersion.name}{' '}
                      ({productVersion.name})
                    </ProductName>
                  </Td>
                  <Td>
                    <Version>{productVersion.productVersion}</Version>
                  </Td>
                  <Td>
                    <StatusContainer>
                      <StatusBadge active={productVersion.active}>
                        {productVersion.active ? 'enabled' : 'disabled'}
                      </StatusBadge>
                      <ToggleSwitch
                        active={productVersion.active}
                        onClick={() => handleToggleActive(productVersion)}
                      >
                        <ToggleSwitchSlider active={productVersion.active} />
                      </ToggleSwitch>
                    </StatusContainer>
                  </Td>
                  <Td>
                    {renderCountryList(productVersion.whitelistedCountries, productVersion.active)}
                  </Td>
                  <Td>
                    {renderCountryList(
                      getBlockedCountries(productVersion.whitelistedCountries),
                      productVersion.active,
                      COUNTRY_LIST_TYPE.BLOCKLIST,
                    )}
                  </Td>
                  <Td>
                    {productVersion.name.toLowerCase().includes('infinicyt') && (
                      <>
                        <ActionButton onClick={() => handleEditWhitelist(productVersion)}>
                          Edit Countries
                        </ActionButton>
                        <ActionButton onClick={() => handleEditAssets(productVersion)}>
                          Edit Assets
                        </ActionButton>
                      </>
                    )}
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableContainer>

      {showCreateModal && (
        <CreateProductVersionModal onSave={handleCreateSave} onCancel={handleCreateCancel} />
      )}

      {modal.isOpen && modal.productVersion && (
        <ModalOverlay>
          <ModalContent>
            <CountryWhitelistEditor
              currentCountries={modal.productVersion.whitelistedCountries}
              onSave={handleWhitelistSave}
              onCancel={hideModal}
            />
          </ModalContent>
        </ModalOverlay>
      )}

      {showAssetsModal && selectedProductVersion && (
        <ModalOverlay>
          <ModalContent>
            <ProductAssetsEditor
              productVersion={selectedProductVersion}
              onSave={handleAssetsSave}
              onCancel={handleAssetsCancel}
            />
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default ProductVersionTable;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px;
  background-color: ${({ theme }) => theme.colors.white};
  /* This is the flex item the super-admin page drops into FjFlexContainer's row.
     A flex item defaults to min-width: auto and refuses to shrink below its
     content, so without this the whole page grows wider than the window and the
     table never overflows anything — no horizontal scrollbar appears and the
     right-hand columns are simply unreachable. */
  min-width: 0;
`;

const ControlBar = styled.div`
  display: flex;
  /* The filter and the button group are both fixed-width content; without
     wrapping they cannot give way on a narrow window and push the page wider
     than the viewport instead. */
  flex-wrap: wrap;
  min-width: 0;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 20px;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black};
  white-space: nowrap;
`;

const FilterSelect = styled.select`
  padding: 8px 32px 8px 12px;
  font-size: 14px;
  border: 1px solid ${({ theme }) => theme.colors.notSelectable};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.black};
  cursor: pointer;
  transition: all 0.2s;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 16px;
  min-width: 200px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.selectableLightBg};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.selectableLightBg};
    outline-offset: 2px;
    border-color: ${({ theme }) => theme.colors.selectableLightBg};
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  margin-right: 20px;
`;

const ButtonGroup = styled.div`
  display: flex;
  /* Buttons carry a min-width, so they wrap onto another line rather than
     overflowing when the bar runs out of room. */
  flex-wrap: wrap;
  min-width: 0;
  gap: 12px;
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
  border: 1px solid ${({ theme }) => theme.colors.notSelectable};
  border-radius: 4px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: ${({ theme }) => theme.colors.white};
`;

const Th = styled.th<{ width?: string; minWidth?: string; maxWidth?: string }>`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.black};
  background-color: ${({ theme }) => theme.colors.tabBackground};
  border-bottom: 2px solid ${({ theme }) => theme.colors.notSelectable};
  position: sticky;
  top: 0;
  ${({ width }) => width && `width: ${width};`}
  ${({ minWidth }) => minWidth && `min-width: ${minWidth};`}
  ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth};`}
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.notSelectable};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.black};
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 0;
`;

const ProductName = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.selectableLightBg};
`;

const Version = styled.span`
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
`;

const CountryList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const CountryBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background-color: ${({ theme }) => theme.colors.selectableLightBg};
  color: ${({ theme }) => theme.colors.white};
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
`;

const AllCountriesText = styled.span`
  font-style: italic;
  color: ${({ theme }) => theme.colors.productTitle};
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.selectableLightBg};
  color: ${({ theme }) => theme.colors.selectableLightBg};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.selectableLightBg};
    color: ${({ theme }) => theme.colors.white};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.selectableLightBg};
    outline-offset: 2px;
  }
  margin-bottom: 6px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
`;

const EmptySubtext = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.productTitle};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Above the table-toolbar dropdown (1002) and the FjTooltip visor (.fj-tooltip-portal .visor,
     1001) - both share this component's stacking context via document.body portals, and a modal
     must render above either if one is left open/lingering underneath it. */
  z-index: 1003;
`;

const ModalContent = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 90%;
  max-height: 90vh;
  overflow: hidden;
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusBadge = styled.span<{ active: boolean }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: lowercase;
  background-color: ${({ active, theme }) =>
    active
      ? (theme.colors as OptionalThemeColors).success || '#4caf50'
      : (theme.colors as OptionalThemeColors).warning || '#ff9800'};
  color: ${({ theme }) => theme.colors.white};
`;

const ToggleSwitch = styled.button<{ active: boolean }>`
  position: relative;
  width: 44px;
  height: 24px;
  background-color: ${({ active, theme }) =>
    active ? (theme.colors as OptionalThemeColors).success || '#4caf50' : '#ccc'};
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  padding: 0;

  &:hover {
    background-color: ${({ active }) => (active ? '#45a049' : '#b3b3b3')};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => (theme.colors as OptionalThemeColors).primary || '#1976d2'};
    outline-offset: 2px;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ToggleSwitchSlider = styled.span<{ active: boolean }>`
  position: absolute;
  top: 2px;
  left: ${({ active }) => (active ? '22px' : '2px')};
  width: 20px;
  height: 20px;
  background-color: white;
  border-radius: 50%;
  transition: left 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const DisabledVersionText = styled.div`
  font-style: italic;
  color: ${({ theme }) => (theme.colors as OptionalThemeColors).warning || '#ff9800'};
  margin-bottom: 8px;
`;
